// ─── Shared paste sanitizer for chat composers ───────────────────────────────
// Preserves text formatting (bold/italic/underline/strike, headings, lists,
// links, paragraphs, line breaks, text color/weight/alignment) while removing
// background and highlight colors plus editor-specific noise (Word/Google Docs
// markup, classes, ids, event handlers, scripts, styles). Used by every
// contentEditable chat composer so paste behaviour stays consistent.

const PASTE_ALLOWED_TAGS = new Set([
  'A', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'MARK',
  'P', 'BR', 'DIV', 'SPAN', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'CODE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SUB', 'SUP',
]);

const PASTE_ALLOWED_STYLE_PROPS = new Set([
  'color', 'font-weight', 'font-style', 'text-decoration',
  'text-decoration-line', 'text-align',
]);

const sanitizeStyleAttr = (styleText) => {
  const kept = [];
  styleText.split(';').forEach((decl) => {
    const idx = decl.indexOf(':');
    if (idx === -1) return;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    if (!prop || !value) return;
    if (!PASTE_ALLOWED_STYLE_PROPS.has(prop)) return;
    // Drop transparent / white text-color that would be invisible on the composer
    if (prop === 'color' && /^(transparent|white|#fff(fff)?|rgba?\(\s*255\s*,\s*255\s*,\s*255)/i.test(value)) return;
    kept.push(`${prop}: ${value}`);
  });
  return kept.join('; ');
};

export const sanitizePastedHtml = (rawHtml) => {
  const doc = new DOMParser().parseFromString(rawHtml, 'text/html');

  const walk = (node) => {
    // Snapshot children first — we mutate the tree during iteration
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const tag = child.tagName;

      if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'META' || tag === 'LINK' ||
          tag === 'TITLE' || tag.includes(':') || tag.startsWith('O:')) {
        child.remove();
        return;
      }

      walk(child);

      if (!PASTE_ALLOWED_TAGS.has(tag)) {
        // Unwrap disallowed element, keeping its (already-sanitized) content
        child.replaceWith(...child.childNodes);
        return;
      }

      // Strip every attribute except href (on anchors) and a filtered style
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name === 'href' && tag === 'A') return;
        if (name === 'style') {
          const cleaned = sanitizeStyleAttr(attr.value);
          if (cleaned) child.setAttribute('style', cleaned);
          else child.removeAttribute('style');
          return;
        }
        child.removeAttribute(attr.name);
      });

      if (tag === 'A') {
        const href = child.getAttribute('href') || '';
        if (!/^(https?:|mailto:|tel:)/i.test(href.trim())) {
          child.replaceWith(...child.childNodes);
          return;
        }
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }
    });
  };

  walk(doc.body);
  return doc.body.innerHTML;
};

// Paste handler for a contentEditable composer. Call from onPaste; pass the
// editor element and a callback that receives the editor's new innerHTML.
export const handleRichEditorPaste = (e, editorEl, onChange) => {
  e.preventDefault();
  const clip = e.clipboardData || window.clipboardData;
  if (!clip) return;
  const html = clip.getData('text/html');
  const plain = clip.getData('text/plain');

  let fragmentHtml;
  if (html) {
    fragmentHtml = sanitizePastedHtml(html);
  } else if (plain) {
    const div = document.createElement('div');
    div.textContent = plain;
    fragmentHtml = div.innerHTML.replace(/\r?\n/g, '<br>');
  } else {
    return;
  }

  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const temp = document.createElement('div');
    temp.innerHTML = fragmentHtml;
    const frag = document.createDocumentFragment();
    let lastNode = null;
    while (temp.firstChild) lastNode = frag.appendChild(temp.firstChild);
    range.insertNode(frag);
    if (lastNode) {
      range.setStartAfter(lastNode);
      range.setEndAfter(lastNode);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  } else if (plain) {
    document.execCommand('insertText', false, plain);
  }

  if (editorEl && typeof onChange === 'function') {
    onChange(editorEl.innerHTML);
  }
};
